import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import VoidExpenseModal from "@/components/accounts/VoidExpenseModal";
import TemplateManager from "@/components/accounts/TemplateManager";
import PendingTemplatesBanner from "@/components/accounts/PendingTemplatesBanner";

const monthTagToday = () => new Date().toISOString().slice(0, 7);

function monthLabel(mtag) {
  if (!mtag || mtag.length < 7) return mtag;
  const [y, m] = mtag.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}

export default function StudioOverheadTab() {
  const [monthTag, setMonthTag] = useState(monthTagToday());
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ category: "", amount: "", description: "", date: monthTagToday() + "-01" });
  const [submitting, setSubmitting] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [error, setError] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/expenses/overhead/summary", { params: { month_tag: monthTag } });
    setSummary(data);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [monthTag]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/catalogs");
      const cats = (data.expense_categories || [])
        .filter((c) => c.active && c.default_type === "studio_overhead")
        .sort((a, b) => a.name.localeCompare(b.name));
      setCategories(cats);
      if (!form.category && cats.length) setForm((f) => ({ ...f, category: cats[0].name }));
    })();
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    setError("");
    if (!form.category) { setError("Pick a category."); return; }
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setError("Amount must be greater than 0."); return; }
    setSubmitting(true);
    try {
      await api.post("/expenses", {
        type: "studio_overhead",
        category: form.category,
        amount: amt,
        description: form.description || null,
        date: form.date,
        month_tag: monthTag,
      });
      toast.success(`Overhead of ${fmtINR(amt)} added`);
      setForm({ category: categories[0]?.name || "", amount: "", description: "", date: monthTag + "-01" });
      setAdding(false);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not add expense.");
    } finally {
      setSubmitting(false);
    }
  };

  const itemsByCategory = useMemo(() => {
    const m = {};
    for (const it of (summary?.items || [])) {
      (m[it.category] = m[it.category] || []).push(it);
    }
    return m;
  }, [summary]);

  const total = summary?.total || 0;

  return (
    <div data-testid="studio-overhead-tab">
      <PendingTemplatesBanner monthTag={monthTag} onConfirmed={refresh} />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-end gap-3">
          <label className="block">
            <span className="kicker block mb-1">Month</span>
            <input
              type="month"
              value={monthTag}
              onChange={(e) => setMonthTag(e.target.value)}
              className="px-3 py-1.5 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="overhead-month-picker"
            />
          </label>
          <div>
            <div className="kicker">TOTAL · {monthLabel(monthTag)}</div>
            <div className="font-display text-2xl" style={{ color: "var(--brand-ink)" }} data-testid="overhead-total">
              {fmtINR(total)}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTemplates((v) => !v)}
            className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase border border-brand-border text-brand-ink"
            data-testid="overhead-toggle-templates"
          >
            {showTemplates ? "Hide templates" : "Manage templates"}
          </button>
          <button
            onClick={() => setAdding(true)}
            className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-white"
            style={{ background: "var(--brand-primary)" }}
            data-testid="overhead-add-button"
          >
            + Add ad-hoc expense
          </button>
        </div>
      </div>

      {showTemplates && <TemplateManager categories={categories} onChanged={refresh} />}

      {adding && (
        <div className="border border-brand-border p-4 mb-4 space-y-2 bg-white" data-testid="overhead-form">
          <div className="kicker mb-2">ADD AD-HOC OVERHEAD</div>
          <div className="grid grid-cols-2 gap-2">
            <label>
              <span className="kicker block mb-1">Category</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-brand-border"
                style={{ borderRadius: 0 }}
                data-testid="overhead-category"
              >
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <label>
              <span className="kicker block mb-1">Amount</span>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-brand-border"
                style={{ borderRadius: 0 }}
                data-testid="overhead-amount"
              />
            </label>
          </div>
          <label className="block">
            <span className="kicker block mb-1">Date</span>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="overhead-date"
            />
          </label>
          <label className="block">
            <span className="kicker block mb-1">Description (optional)</span>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="overhead-description"
            />
          </label>
          {error && <p className="text-xs text-rose-700" data-testid="overhead-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { setAdding(false); setError(""); }}
              disabled={submitting}
              className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-brand-muted"
              data-testid="overhead-cancel"
            >Cancel</button>
            <button
              onClick={submit}
              disabled={submitting}
              className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase text-white"
              style={{ background: "var(--brand-primary)" }}
              data-testid="overhead-save"
            >{submitting ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}

      {/* Grouped by category */}
      {Object.keys(itemsByCategory).length === 0 ? (
        <div className="bg-white border border-brand-border p-12 text-center" data-testid="overhead-empty">
          <p className="text-sm italic text-brand-muted">
            No overhead recorded for {monthLabel(monthTag)} yet.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="overhead-groups">
          {(summary?.by_category || []).map((g) => (
            <div key={g.category} className="bg-white border border-brand-border" data-testid={`overhead-group-${g.category}`}>
              <div className="flex items-center justify-between p-3 border-b border-brand-border">
                <div>
                  <span className="font-medium" style={{ color: "var(--brand-ink)" }}>{g.category}</span>
                  <span className="text-xs text-brand-muted ml-2">{g.count} item{g.count > 1 ? "s" : ""}</span>
                </div>
                <div className="text-sm font-medium" style={{ color: "var(--brand-ink)" }}>{fmtINR(g.total)}</div>
              </div>
              <ul className="divide-y divide-brand-border">
                {(itemsByCategory[g.category] || []).map((it) => (
                  <li key={it.id} className="p-3 flex items-start justify-between gap-3" data-testid={`overhead-item-${it.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">
                        {it.description || (it.is_recurring_instance ? "Recurring · " + it.category : it.category)}
                        {it.is_recurring_instance && (
                          <span className="ml-2 px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase"
                                style={{ background: "var(--brand-paper)", color: "var(--brand-muted)" }}>
                            TEMPLATE
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-brand-muted mt-0.5">{fmtDate(it.date)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium" style={{ color: "var(--brand-danger)" }}>− {fmtINR(it.amount)}</div>
                      <button
                        onClick={() => setVoidTarget(it)}
                        className="text-[10px] underline text-brand-muted mt-1"
                        data-testid={`overhead-void-${it.id}`}
                      >Void</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {voidTarget && (
        <VoidExpenseModal
          expense={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={() => { setVoidTarget(null); refresh(); }}
        />
      )}
    </div>
  );
}
